import { ToolboxComponentOption, VisualMapComponentOption } from 'echarts';
import type { BarSeriesOption, LineSeriesOption, PieSeriesOption } from 'echarts/charts';
import type {
  DatasetComponentOption,
  DataZoomComponentOption,
  GridComponentOption,
  LegendComponentOption,
  TooltipComponentOption,
  AxisPointerComponentOption,
} from 'echarts/components';
import type { ComposeOption } from 'echarts/core';
import type {
  AngleAxisOption,
  AriaOption,
  AxisPointerOption,
  BrushOption,
  CalendarOption,
  CallbackDataParams,
  DatasetOption,
  ECBasicOption,
  EChartsOption,
  GeoOption,
  GraphicComponentLooseOption,
  GridOption,
  ParallelCoordinateSystemOption,
  PolarOption,
  RadarOption,
  RadiusAxisOption,
  SingleAxisOption,
  TimelineOption,
  TitleOption,
  TooltipOption,
  XAXisOption,
  YAXisOption,
} from 'echarts/types/dist/shared';
import { ParallelAxisOption } from 'echarts/types/src/coord/parallel/AxisModel';
import { MediaUnit } from 'echarts/types/src/util/types';
import type { Observable } from 'rxjs';

import { createProviderToken } from '@core/common-legacy';
// eslint-disable-next-line boundaries/element-types
import type { Theme } from '@core/theme-util'; // --FIXME

export type ReportTooltipNormalizedParams = CallbackDataParams[];

export type ReportTooltipComponentOption = Omit<TooltipComponentOption, 'formatter'> & {
  formatter: (params: ReportTooltipNormalizedParams) => any;
};

// For simplicity multiple union types are removed, to allow only T, not T | T[]
export type ReportsEChartsOption = {
  media?: MediaUnit[];
  dataset?: DatasetOption;
  aria?: AriaOption;
  title?: TitleOption;
  grid?: GridOption;
  radar?: RadarOption;
  polar?: PolarOption;
  geo?: GeoOption;
  angleAxis?: AngleAxisOption;
  radiusAxis?: RadiusAxisOption;
  xAxis?: XAXisOption;
  yAxis?: YAXisOption;
  singleAxis?: SingleAxisOption;
  parallel?: ParallelCoordinateSystemOption;
  parallelAxis?: ParallelAxisOption;
  calendar?: CalendarOption;
  toolbox?: ToolboxComponentOption;
  tooltip?: TooltipOption;
  axisPointer?: AxisPointerOption;
  brush?: BrushOption;
  timeline?: TimelineOption;
  legend?: LegendComponentOption;
  dataZoom?: DataZoomComponentOption | DataZoomComponentOption[];
  visualMap?: VisualMapComponentOption;
  graphic?: GraphicComponentLooseOption | GraphicComponentLooseOption[];
  series?: EChartsOption['series'];
  options?: EChartsOption[];
  baseOption?: EChartsOption;
};

// Legacy options for old visualizations
export type ReportsChartOptions = {
  dataset: DatasetComponentOption;
  series: Array<LineSeriesOption | BarSeriesOption | PieSeriesOption>;
  xAxis?: XAXisOption;
  yAxis?: YAXisOption;
  stack?: string | number | null;
  tooltip?: ReportTooltipComponentOption;
  axisPointer?: AxisPointerComponentOption;
  legend?: LegendComponentOption;
  graphic?: EChartsOption['graphic'];
  media?: EChartsOption['media'];
  dataZoom?: DataZoomComponentOption[];
  grid?: GridComponentOption;
  getNgTooltip?: ECBasicOption['getNgTooltip'];
};

export type ReportsChartTheme = {
  colors: EChartsOption['color'];
  textColor: string;
  splitLinesColor: string;
  ticksColor: string;
  pieChartLabelValue: string;
  labelBorder: string;
  sliderBgColor: string;
  sliderColor: string;
  sliderBorder: string;
  pageIconColor: string;
  pageIconDisabledColor: string;
  pageTextColor: string;
};

export type ReportsAxisOptions = ComposeOption<XAXisOption | YAXisOption>;

type AxisType = 'value' | 'category' | 'time';

export type ReportsBarChartOptions<X extends AxisType, Y extends AxisType> = ReportsChartOptions & {
  series: BarSeriesOption[];
  dataZoom?: DataZoomComponentOption[];
  grid?: GridComponentOption;
  xAxis: ReportsAxisOptions & { type: X };
  yAxis: ReportsAxisOptions & { type: Y };
};

export type ReportsBarChartLabelOptions = ReportsBarChartOptions<
  'value',
  'time' | 'category'
>['series'][0]['label'];

export type ReportsLineChartOptions<
  X extends AxisType,
  Y extends AxisType,
> = ReportsChartOptions & {
  series: LineSeriesOption[];
  dataZoom?: DataZoomComponentOption[];
  grid?: GridComponentOption;
  xAxis: ReportsAxisOptions & { type: X };
  yAxis: ReportsAxisOptions & { type: Y };
};

export type ReportsCategoryAxisOptions = { type: 'category' } & ReportsAxisOptions;
export type ReportsTimeAxisOptions = { type: 'time' } & ReportsAxisOptions;

export type ReportsPieChartOptions = ReportsChartOptions & {
  series: [PieSeriesOption];
};

export const { provide: provideReportsChartTheme, inject: injectReportsChartTheme } =
  createProviderToken<Observable<Theme>>('ReportsChartTheme')();
